import { supabase, isConfigured } from './supabase';

export interface PayoutSummary {
  total_pending_cents: number;
  ready_to_pay_count: number;
  paid_this_month_cents: number;
}

export interface PayableAffiliate {
  id: string;
  user_id: string;
  email: string;
  code: string;
  paypal_email: string | null;
  wise_email: string | null;
  preferred_payout_method: string | null;
  pending_amount_cents: number;
  pending_count: number;
}

export interface PartnerPayout {
  id: string;
  affiliate_id: string;
  affiliate_email: string;
  affiliate_code: string;
  amount_cents: number;
  currency: string;
  payout_method: 'paypal' | 'wise';
  payout_reference: string | null;
  status: 'pending' | 'sent' | 'paid' | 'failed';
  created_at: string;
  paid_at: string | null;
}

export interface PayoutCommission {
  id: string;
  referred_email: string;
  purchase_id: string;
  purchase_amount_cents: number;
  commission_amount_cents: number;
  created_at: string;
}

export interface PayoutDetail {
  payout: PartnerPayout & {
    paypal_email: string | null;
    wise_email: string | null;
  };
  commissions: PayoutCommission[];
}

export async function getPayoutSummary(): Promise<PayoutSummary | null> {
  if (!isConfigured) return null;
  try {
    const { data, error } = await supabase.rpc('admin_get_payout_summary');
    if (error) {
      console.error('[payouts] summary error:', error);
      return null;
    }
    if (data?.success) {
      return {
        total_pending_cents: data.total_pending_cents ?? 0,
        ready_to_pay_count: data.ready_to_pay_count ?? 0,
        paid_this_month_cents: data.paid_this_month_cents ?? 0,
      };
    }
    return null;
  } catch (err) {
    console.error('[payouts] summary exception:', err);
    return null;
  }
}

export async function getPayableAffiliates(): Promise<PayableAffiliate[]> {
  if (!isConfigured) return [];
  try {
    const { data, error } = await supabase.rpc('admin_get_payable_affiliates');
    if (error) {
      console.error('[payouts] payable error:', error);
      return [];
    }
    return data?.success ? (data.affiliates as PayableAffiliate[]) : [];
  } catch (err) {
    console.error('[payouts] payable exception:', err);
    return [];
  }
}

export async function createPayout(
  affiliateId: string,
  payoutMethod?: string
): Promise<{ success: boolean; payout_id?: string; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };
  try {
    const { data, error } = await supabase.rpc('admin_create_payout', {
      p_affiliate_id: affiliateId,
      p_payout_method: payoutMethod || null,
    });
    if (error) {
      console.error('[payouts] create error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; payout_id?: string; error?: string };
  } catch (err) {
    console.error('[payouts] create exception:', err);
    return { success: false, error: 'Failed to create payout' };
  }
}

export async function getPayouts(
  status?: string,
  limit: number = 50
): Promise<PartnerPayout[]> {
  if (!isConfigured) return [];
  try {
    const { data, error } = await supabase.rpc('admin_get_payouts', {
      p_status: status || null,
      p_limit: limit,
    });
    if (error) {
      console.error('[payouts] list error:', error);
      return [];
    }
    return data?.success ? (data.payouts as PartnerPayout[]) : [];
  } catch (err) {
    console.error('[payouts] list exception:', err);
    return [];
  }
}

export async function getPayoutDetail(payoutId: string): Promise<PayoutDetail | null> {
  if (!isConfigured) return null;
  try {
    const { data, error } = await supabase.rpc('admin_get_payout_detail', {
      p_payout_id: payoutId,
    });
    if (error) {
      console.error('[payouts] detail error:', error);
      return null;
    }
    if (data?.success) {
      return {
        payout: data.payout as PayoutDetail['payout'],
        commissions: (data.commissions ?? []) as PayoutCommission[],
      };
    }
    return null;
  } catch (err) {
    console.error('[payouts] detail exception:', err);
    return null;
  }
}

export async function confirmPayout(
  payoutId: string,
  reference?: string
): Promise<{ success: boolean; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };
  try {
    const { data, error } = await supabase.rpc('admin_confirm_payout', {
      p_payout_id: payoutId,
      p_reference: reference || null,
    });
    if (error) {
      console.error('[payouts] confirm error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; error?: string };
  } catch (err) {
    console.error('[payouts] confirm exception:', err);
    return { success: false, error: 'Failed to confirm payout' };
  }
}

export async function failPayout(
  payoutId: string
): Promise<{ success: boolean; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };
  try {
    const { data, error } = await supabase.rpc('admin_fail_payout', {
      p_payout_id: payoutId,
    });
    if (error) {
      console.error('[payouts] fail error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; error?: string };
  } catch (err) {
    console.error('[payouts] fail exception:', err);
    return { success: false, error: 'Failed to mark payout as failed' };
  }
}

export async function updateAffiliatePayoutDetails(
  affiliateId: string,
  paypalEmail: string | null,
  wiseEmail: string | null,
  preferredMethod: string | null
): Promise<{ success: boolean; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };
  try {
    const { data, error } = await supabase.rpc('admin_update_affiliate_payout_details', {
      p_affiliate_id: affiliateId,
      p_paypal_email: paypalEmail,
      p_wise_email: wiseEmail,
      p_preferred_payout_method: preferredMethod,
    });
    if (error) {
      console.error('[payouts] update details error:', error);
      return { success: false, error: error.message };
    }
    return data as { success: boolean; error?: string };
  } catch (err) {
    console.error('[payouts] update details exception:', err);
    return { success: false, error: 'Failed to update payout details' };
  }
}

export function formatUSD(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}
